import { Injectable } from '@angular/core';
import moment from 'moment';
import { ConductTimelog, ConductProjectSummary } from '../interface/conduct-timelog';

export interface SuperSeeOverlapDetails {
  activeSecs: number;
  idleSecs?: number;
  offlineSecs?: number;
  activeText?: string;
  idleText?: string;
  offlineText?: string;
  superSeeTimeRangeText: string;
  superSeeDurationText: string;
}

export interface ConductProjectColorItem {
  name: string;
  color: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConductTimelogService {
  /**
   * Colors excluded from Conduct timeline capsules to prevent clashing
   * with SuperSee productive/idle/offline theme colors.
   */
  static readonly EXCLUDED_THEME_COLORS = new Set<string>([
    '#2143bc',
    '#6521bc',
    '#a621bc',
    '#f9c80e'
  ]);

  /**
   * Formats duration into two-digit zero-padded "04 hrs 01 mins" format.
   * Example: 3600 -> "01 hrs 00 mins", 2700 -> "00 hrs 45 mins", 14460 -> "04 hrs 01 mins".
   */
  formatDurationHrsMins(seconds: number): string {
    const totalSecs = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const hStr = String(h).padStart(2, '0');
    const mStr = String(m).padStart(2, '0');
    return `${hStr} hrs ${mStr} mins`;
  }

  /**
   * Formats duration in compact form (e.g. "39 mins", "1 hr", "1 hr 15 mins").
   */
  formatActiveOverlapDisplay(seconds: number): string {
    const totalSecs = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }

  /**
   * Parses various time formats (HH:mm, hh:mm AM/PM, ISO string, timestamp) to minutes from midnight.
   */
  parseTimeToMinutes(timeVal: unknown): number {
    if (!timeVal) return 0;
    const str = String(timeVal).trim();

    // 12-hour format: "10:30 AM" or "02:15 pm"
    const match12 = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (match12) {
      let hours = parseInt(match12[1], 10);
      const minutes = parseInt(match12[2], 10);
      const mer = match12[3].toUpperCase();
      if (mer === 'PM' && hours < 12) hours += 12;
      if (mer === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }

    // 24-hour format: "14:30" or "09:15:00"
    const match24 = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (match24) {
      const hours = parseInt(match24[1], 10);
      const minutes = parseInt(match24[2], 10);
      return hours * 60 + minutes;
    }

    // Full datetime: "2026-10-14 10:30:00" or ISO format
    if (str.includes('T') || str.includes('-') || str.includes('/')) {
      const parsed = moment(str);
      if (parsed.isValid()) {
        return parsed.hours() * 60 + parsed.minutes();
      }
    }

    return 0;
  }

  /**
   * Converts minutes from midnight into 12-hour format string (e.g. 630 -> "10:30 AM").
   */
  minutesTo12h(totalMinutes: number): string {
    const norm = ((Math.floor(totalMinutes) % 1440) + 1440) % 1440;
    const h24 = Math.floor(norm / 60);
    const m = norm % 60;
    const mer = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${mer}`;
  }

  /**
   * Extracts the start minute of a Conduct timelog from from_time or defaults to 09:00 AM (540).
   */
  parseLogStartMinute(log: ConductTimelog): number {
    if (log.from_time) {
      return this.parseTimeToMinutes(log.from_time);
    }
    return 9 * 60;
  }

  /**
   * Calculates the end minute of a Conduct timelog from end_time, duration, or fallback.
   */
  parseLogEndMinute(log: ConductTimelog, startMin: number): number {
    if (log.end_time) {
      const eMin = this.parseTimeToMinutes(log.end_time);
      if (eMin > startMin) return eMin;
    }
    const durSecs = this.getDurationSeconds(log, 0);
    if (durSecs > 0) {
      return startMin + Math.round(durSecs / 60);
    }
    return startMin + 45;
  }

  /**
   * Extracts the duration in seconds of a Conduct timelog.
   */
  getDurationSeconds(log: ConductTimelog, fallbackSecs: number = 0): number {
    if (log.duration_time_second !== undefined && log.duration_time_second !== null) {
      const parsed = parseInt(String(log.duration_time_second), 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    if (log.worked_hours && typeof log.worked_hours === 'string' && log.worked_hours.includes(':')) {
      const parts = log.worked_hours.split(':').map((p) => parseInt(p, 10) || 0);
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 3600 + parts[1] * 60;
    }
    return fallbackSecs;
  }

  /**
   * Calculates actual SuperSee active (productive) overlap details for a given task window.
   */
  calculateSuperSeeOverlapDetails(
    daySessions: unknown[],
    taskStartMin: number,
    taskEndMin: number
  ): SuperSeeOverlapDetails {
    const fallbackRange = `${this.minutesTo12h(taskStartMin)} – ${this.minutesTo12h(taskEndMin)}`;
    const totalTaskSecs = Math.max(0, (taskEndMin - taskStartMin) * 60);

    if (!Array.isArray(daySessions) || !daySessions.length) {
      return {
        activeSecs: 0,
        idleSecs: 0,
        offlineSecs: totalTaskSecs,
        activeText: this.formatActiveOverlapDisplay(0),
        idleText: this.formatActiveOverlapDisplay(0),
        offlineText: this.formatActiveOverlapDisplay(totalTaskSecs),
        superSeeTimeRangeText: fallbackRange,
        superSeeDurationText: this.formatDurationHrsMins(0)
      };
    }

    const taskLen = Math.max(0, taskEndMin - taskStartMin);
    // 0 = offline, 1 = idle, 2 = productive
    const minuteStates = new Uint8Array(taskLen);

    daySessions.forEach((rawSess: unknown) => {
      const sess = rawSess as Record<string, unknown> | null;
      if (!sess) return;

      const isProductive =
        sess['label'] === 'usage' ||
        (sess['usage'] && sess['usage'] !== '00:00:00' && sess['usage'] !== '0');
      const isIdle =
        sess['label'] === 'idle' ||
        (sess['idle'] && sess['idle'] !== '00:00:00' && (!sess['usage'] || sess['usage'] === '00:00:00'));

      if (!isProductive && !isIdle) return;

      const inTime = sess['in'] ?? sess['inTime'];
      const outTime = sess['out'] ?? sess['outTime'];
      if (!inTime || !outTime) return;

      const sessStartMin = this.parseTimeToMinutes(inTime);
      let sessEndMin = this.parseTimeToMinutes(outTime);
      if (sessEndMin <= sessStartMin) {
        sessEndMin = sessStartMin + 1;
      }

      const overlapStart = Math.max(taskStartMin, sessStartMin);
      const overlapEnd = Math.min(taskEndMin, sessEndMin);

      if (overlapEnd > overlapStart) {
        for (let m = overlapStart; m < overlapEnd; m++) {
          const idx = m - taskStartMin;
          if (isProductive) {
            minuteStates[idx] = 2;
          } else if (isIdle && minuteStates[idx] !== 2) {
            minuteStates[idx] = 1;
          }
        }
      }
    });

    let activeMins = 0;
    let idleMins = 0;
    let offlineMins = 0;
    let minActive: number | null = null;
    let maxActive: number | null = null;

    for (let i = 0; i < taskLen; i++) {
      const state = minuteStates[i];
      const curMin = taskStartMin + i;
      if (state === 2) {
        activeMins++;
        if (minActive === null || curMin < minActive) {
          minActive = curMin;
        }
        if (maxActive === null || curMin + 1 > maxActive) {
          maxActive = curMin + 1;
        }
      } else if (state === 1) {
        idleMins++;
      } else {
        offlineMins++;
      }
    }

    const activeSecs = activeMins * 60;
    const idleSecs = idleMins * 60;
    const offlineSecs = offlineMins * 60;

    const superSeeTimeRangeText =
      minActive !== null && maxActive !== null && maxActive > minActive
        ? `${this.minutesTo12h(minActive)} – ${this.minutesTo12h(maxActive)}`
        : fallbackRange;

    return {
      activeSecs,
      idleSecs,
      offlineSecs,
      activeText: this.formatActiveOverlapDisplay(activeSecs),
      idleText: this.formatActiveOverlapDisplay(idleSecs),
      offlineText: this.formatActiveOverlapDisplay(offlineSecs),
      superSeeTimeRangeText,
      superSeeDurationText: this.formatDurationHrsMins(activeSecs)
    };
  }

  /**
   * Returns active overlapping seconds for a task window.
   */
  calculateSuperSeeOverlapSeconds(daySessions: unknown[], taskStartMin: number, taskEndMin: number): number {
    return this.calculateSuperSeeOverlapDetails(daySessions, taskStartMin, taskEndMin).activeSecs;
  }

  /**
   * Primary black color used for all Conduct timeline capsules.
   */
  static readonly PRIMARY_CONDUCT_COLOR = '#111827';

  /**
   * Assigns the single consistent black color for Conduct timeline capsules across the whole timeline.
   */
  getConductLogColor(
    log?: ConductTimelog,
    colorMap?: Map<string, string>,
    palette?: string[]
  ): string {
    return ConductTimelogService.PRIMARY_CONDUCT_COLOR;
  }

  /**
   * Generates a distinct list of projects with their associated colors for legend display.
   */
  getDistinctConductProjects(
    logs: ConductTimelog[] | undefined,
    colorMap?: Map<string, string>,
    palette?: string[]
  ): ConductProjectColorItem[] {
    if (!logs || !logs.length) return [];

    const seen = new Set<string>();
    const list: ConductProjectColorItem[] = [];

    logs.forEach((l) => {
      const pName = (l.project_name || l.project_id || 'General Tasks').trim();
      const key = pName.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        list.push({ name: pName, color: ConductTimelogService.PRIMARY_CONDUCT_COLOR });
      }
    });

    return list;
  }

  /**
   * Checks whether a timelog falls on day 2 in a 48-hour range.
   */
  isLogOnDay2(log: ConductTimelog, startDate?: string | Date, endDate?: string | Date): boolean {
    if (!startDate || !endDate) return false;
    const logDate = log.log_date || log.date;
    if (!logDate) return false;
    const endStr = moment(endDate).format('YYYY-MM-DD');
    return moment(logDate).format('YYYY-MM-DD') === endStr;
  }
}
